import { useTheme } from "@emotion/react";
import { Email, LockOutlined, Send } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Grid,
  InputAdornment,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMicrospecDynamicUIContext } from "../GlobalProvider";
import { getOrDefault } from "../Helpers/Utils";
import { useMicrospecThemeLanguageContext } from "../Themes";
import AuthWrapper from "./AuthWrapper";
import { t } from "i18next";

const ForgotPassword = ({ config }) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo')
  const { localizationProvider } = useMicrospecThemeLanguageContext()
  const { cloudProvider } = useMicrospecDynamicUIContext()

  // Extract enableRegister from config
  const enableRegister = getOrDefault(config?.application?.auth.enableRegister, true);
  const { unauthenticatedSendPasswordResetLink } = cloudProvider
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  // Generate gradients from theme colors
  const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true)
    setError(false)
    setSuccess(false)
    const data = new FormData(event.currentTarget);
    const email = data.get('email')
    const result = await unauthenticatedSendPasswordResetLink(email)
    setSuccess(result)
    setError(!result)
    setLoading(false)
  };

  return (
    <AuthWrapper key={ForgotPassword.name} redirectTo={redirectTo}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar
          sx={{
            m: '0 auto',
            mb: 2,
            width: 56,
            height: 56,
            background: primaryGradient,
            boxShadow: `0 8px 32px ${theme.palette.primary.main}30`
          }}
        >
          <LockOutlined sx={{ fontSize: 28 }} />
        </Avatar>
        <Typography
          component="h1"
          variant="h4"
          sx={{
            fontWeight: 700,
            background: primaryGradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          {localizationProvider.translateByType({
            type: "Internal",
            text: "forgotPassword"
          })}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontSize: '1.1rem', textAlign: 'center' }}
        >
          {success
            ? t('resetLinkSuccessfullySent')
            : t('getResetLink')
          }
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={localizationProvider.translateByType({
            type: "Internal",
            text: "emailAddress"
          })}
          name="email"
          autoComplete="email"
          autoFocus
          variant="outlined"
          disabled={success}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 4px 20px ${theme.palette.primary.main}20`
              },
              '&.Mui-focused': {
                boxShadow: `0 4px 20px ${theme.palette.primary.main}30`
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email color="action" />
              </InputAdornment>
            ),
          }}
        />

        {success && (
          <Alert
            severity="success"
            sx={{
              mb: 2,
              borderRadius: 2,
              border: '1px solid rgba(76, 175, 80, 0.2)',
              backgroundColor: 'rgba(76, 175, 80, 0.1)'
            }}
          >
            {t('resetLinkSuccessfullySent')}
          </Alert>
        )}

        {error && (
          <Alert
            severity='error'
            sx={{
              mb: 2,
              borderRadius: 2,
              border: '1px solid rgba(211, 47, 47, 0.2)'
            }}
          >
            {t('errorTryLater')}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || success}
          sx={{
            mt: 2,
            mb: 3,
            py: 1.5,
            borderRadius: 3,
            background: success
              ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
              : primaryGradient,
            boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: success ? 'none' : 'translateY(-2px)',
              boxShadow: success
                ? '0 8px 32px rgba(76, 175, 80, 0.3)'
                : `0 12px 40px ${theme.palette.primary.main}40`,
              background: success
                ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
            },
            '&:disabled': {
              background: success
                ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                : 'rgba(0, 0, 0, 0.12)',
              transform: 'none',
              boxShadow: success
                ? '0 8px 32px rgba(76, 175, 80, 0.3)'
                : 'none',
              color: success ? 'white' : 'rgba(0, 0, 0, 0.26)'
            }
          }}
          startIcon={success ? <Send /> : loading ? null : <Send />}
        >
          {loading
            ? t('sending')
            : success
              ? t('sentSuccessfully')
              : localizationProvider.translateByType({
                type: "Internal",
                text: "getResetLink"
              })
          }
        </Button>

        <Grid container spacing={8} sx={{ justifyContent: 'space-between' }}>
          <Grid item xs={6}>
            <Link
              textAlign={"end"}
              href={redirectTo ? "/login?redirectTo=" + redirectTo : "/login"}
              variant="body2"
              sx={{
                color: theme.palette.primary.main,
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: theme.palette.primary.dark,
                  textDecoration: 'underline'
                }
              }}
            >
              <Typography>
                {localizationProvider.translateByType({
                  type: "Internal",
                  text: "signIn"
                })}
              </Typography>
            </Link>
          </Grid>
          {enableRegister && (
            <Grid item xs={6}>
              <Link
                textAlign={"start"}
                href={redirectTo ? "/register?redirectTo=" + redirectTo : "/register"}
                variant="body2"
                sx={{
                  textDecoration: 'none',
                  fontWeight: 500,
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                <Typography>
                  {localizationProvider.translateByType({
                    type: "Internal",
                    text: "createAccount"
                  }) || "Create Account"}
                </Typography>
              </Link>
            </Grid>
          )}
        </Grid>
      </Box>
    </AuthWrapper>
  );
};

export default ForgotPassword;
